import { createContext, Fragment, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';

/** Pages with a dynamic title (an invoice number, a customer name) register it here for the crumb trail. */
const CrumbCtx = createContext<{ labels: Record<string, string>; set: (path: string, label: string) => void } | null>(null);

export function CrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const value = useMemo(() => ({ labels, set: (p: string, l: string) => setLabels((x) => (x[p] === l ? x : { ...x, [p]: l })) }), [labels]);
  return <CrumbCtx.Provider value={value}>{children}</CrumbCtx.Provider>;
}

export function useCrumb(label: string | undefined) {
  const ctx = useContext(CrumbCtx);
  const { pathname } = useLocation();
  useEffect(() => {
    if (label) ctx?.set(pathname, label);
  }, [label, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
}

const SEGMENT_LABELS: Record<string, string> = {
  invoices: 'Invoices',
  customers: 'Customers',
  products: 'Products & services',
  imports: 'Import centre',
  reports: 'Reports',
  settings: 'Settings',
  new: 'New',
  edit: 'Edit',
  outstanding: 'Outstanding balances',
  payments: 'Payments',
  exports: 'Exports',
  business: 'Business & taxpayers',
  branches: 'Branches',
  team: 'Team & roles',
  integration: 'NRS integration',
  security: 'Security',
  audit: 'Activity log',
};

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const ctx = useContext(CrumbCtx);
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return <span className="text-sm font-medium">Dashboard</span>;
  const crumbs = parts
    .map((seg, i) => {
      const path = `/${parts.slice(0, i + 1).join('/')}`;
      const isId = /^[0-9a-f-]{36}$/i.test(seg);
      return { path, isId, label: ctx?.labels[path] ?? SEGMENT_LABELS[seg] ?? (isId ? '…' : seg) };
    })
    // An id with no registered title is just noise in the trail; the last crumb still shows.
    .filter((c, i, all) => !(c.isId && c.label === '…' && i < all.length - 1));
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink asChild>
            <Link to="/dashboard">Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:block" />
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={c.path}>
              <BreadcrumbItem className={last ? undefined : 'hidden sm:inline-flex'}>
                {last ? (
                  <BreadcrumbPage className="max-w-[40vw] truncate">{c.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={c.path}>{c.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!last && <BreadcrumbSeparator className="hidden sm:block" />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
